import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Globe, Volume2, VolumeX, ExternalLink, MessageSquare } from 'lucide-react';
import { useAIAssistant } from '../contexts/AIAssistantContext';
import { t } from '../utils/translations';
import { buildAgentWhatsAppUrl, WHATSAPP_AGENT_DISPLAY } from '../utils/assistantFormat';

/** Bouton + panneau d’aide Nimba — présence légère, au-dessus de la nav mobile. */
function AIAssistantWidget() {
  const {
    isOpen,
    messages,
    currentMessage,
    setCurrentMessage,
    isTyping,
    language,
    isVoiceEnabled,
    sendMessage,
    openAssistant,
    closeAssistant,
    toggleLanguage,
    toggleVoice,
    quickSuggestions
  } = useAIAssistant();
  const messagesEndRef = useRef(null);
  const [fabReady, setFabReady] = useState(false);

  // Apparition retardée : moins d’intrusion au premier paint
  useEffect(() => {
    const timer = setTimeout(() => setFabReady(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(currentMessage);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const fabBottom = 'calc(5.75rem + env(safe-area-inset-bottom, 0px))';

  return (
    <>
      {!isOpen && fabReady && (
        <motion.button
          type="button"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          whileTap={{ scale: 0.95 }}
          onClick={openAssistant}
          aria-label={t(language, 'aiAssistantWidget.fabLabel')}
          title={t(language, 'aiAssistantWidget.fabLabel')}
          style={{
            position: 'fixed',
            bottom: fabBottom,
            right: '0.85rem',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.35rem 0.7rem 0.35rem 0.35rem',
            background: 'rgba(12, 61, 114, 0.86)',
            color: '#ffffff',
            borderRadius: '999px',
            border: '1px solid rgba(255, 255, 255, 0.28)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.02em'
          }}
        >
          <span
            style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              background: 'rgba(255,255,255,0.12)'
            }}
          >
            <img
              src="/nimba.jpeg"
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </span>
          <span style={{ paddingRight: '0.15rem' }}>
            {t(language, 'aiAssistantWidget.fabLabel')}
          </span>
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            className="fixed right-3 z-[1100] w-[min(100vw-1.5rem,380px)] h-[min(70vh,520px)] bg-white rounded-2xl shadow-xl border border-gray-200 flex flex-col overflow-hidden"
            style={{ bottom: fabBottom }}
          >
            <div className="text-white p-3.5 flex items-center justify-between" style={{ background: 'rgba(12, 61, 114, 0.92)' }}>
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 bg-white/15 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src="/nimba.jpeg"
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-sm leading-tight">Nimba</h3>
                  <p className="text-[11px] opacity-90">
                    {t(language, 'aiAssistantWidget.headerSubtitle')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-0.5">
                <button
                  type="button"
                  onClick={toggleVoice}
                  className="p-2 hover:bg-white/15 rounded-lg transition-colors"
                  title={
                    isVoiceEnabled
                      ? t(language, 'aiAssistantWidget.voiceDisable')
                      : t(language, 'aiAssistantWidget.voiceEnable')
                  }
                >
                  {isVoiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={toggleLanguage}
                  className="p-2 hover:bg-white/15 rounded-lg transition-colors"
                  title={t(language, 'aiAssistantWidget.changeLanguage')}
                >
                  <Globe className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={closeAssistant}
                  className="p-2 hover:bg-white/15 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3.5 space-y-3 bg-[#faf9f7]">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                      message.type === 'user'
                        ? 'bg-[rgba(12,61,114,0.86)] text-white ml-auto'
                        : 'bg-white text-gray-800 border border-gray-100 shadow-sm'
                    }`}
                  >
                    <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
                    {message.type === 'bot' && (
                      <div className="flex items-center mt-2 pt-2 border-t border-gray-100">
                        <img src="/nimba.jpeg" alt="" className="w-4 h-4 rounded-full mr-2" />
                        <span className="text-xs text-gray-500">Nimba</span>
                      </div>
                    )}

                    {message.needsWhatsApp && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">
                          WhatsApp {WHATSAPP_AGENT_DISPLAY}
                        </p>
                        <a
                          href={buildAgentWhatsAppUrl(message.userQuestion, language)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white px-3 py-2 rounded-lg text-sm font-medium"
                        >
                          <MessageSquare className="h-4 w-4" />
                          {t(language, 'aiAssistantWidget.contactAgent')}
                        </a>
                      </div>
                    )}

                    {message.category && message.type === 'bot' && (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs opacity-70">{message.category}</p>
                        {message.source && (
                          <a
                            href={message.source}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#0c3d72] hover:underline flex items-center gap-1"
                          >
                            {t(language, 'aiAssistantWidget.readMore')} <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-100 p-3 rounded-2xl shadow-sm">
                    <div className="flex items-center space-x-2">
                      <img src="/nimba.jpeg" alt="" className="w-5 h-5 rounded-full" />
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-[#0c3d72] rounded-full animate-bounce" />
                        <div
                          className="w-1.5 h-1.5 bg-[#0c3d72]/70 rounded-full animate-bounce"
                          style={{ animationDelay: '0.1s' }}
                        />
                        <div
                          className="w-1.5 h-1.5 bg-[#0c3d72]/40 rounded-full animate-bounce"
                          style={{ animationDelay: '0.2s' }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">
                      {t(language, 'aiAssistantWidget.typing')}
                    </p>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-3.5 py-2.5 border-t border-gray-100">
                <p className="text-[11px] text-gray-500 mb-1.5">
                  {t(language, 'aiAssistantWidget.quickSuggestions')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {quickSuggestions &&
                    quickSuggestions.slice(0, 3).map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="text-[11px] bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-md transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-3 border-t border-gray-100 bg-white">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  placeholder={t(language, 'aiAssistantWidget.inputPlaceholder')}
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0c3d72]/30 focus:border-[#0c3d72] text-sm"
                />
                <button
                  type="submit"
                  disabled={!currentMessage.trim()}
                  className="bg-[rgba(12,61,114,0.86)] text-white p-2.5 rounded-xl hover:bg-[rgba(12,61,114,0.94)] disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5 text-center">
                {t(language, 'aiAssistantWidget.bottomTip')}
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default AIAssistantWidget;
